/**
 * SettingsPage — 6 sections, one visible at a time.
 *
 *   voice     — voice picker + preview + greeting toggle. Wired to /api/settings.
 *   qoryn     — instruction overlay (composes with base prompt; never mutates
 *               version-controlled assets). Wired to /api/settings.
 *   memory    — placeholder controls. Honest.
 *   data      — placeholder retention controls. Honest.
 *   account   — email display, change password (via supabase-js), sign out.
 *   advanced  — model/voice/provider diagnostics — the ONLY place these
 *               strings surface in the UI. Jobs viewer stub.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    Mic, Play, Pause, Loader2, Save, Volume2, Bell, BellOff, Info,
    Brain, Database, User, Cpu, Check, Eye, EyeOff, LogOut, KeyRound,
} from "lucide-react";
import EmptyState from "@/components/EmptyState";
import FounderCapabilityStatusView from "@/components/settings/FounderCapabilityStatusView";
import { getSettings, patchSettings, updatePassword, getPromptPreview } from "@/lib/settings";
import { fetchVoices, fetchHealth, fetchCapabilities, API } from "@/lib/api";
import { useAuth } from "@/auth/AuthProvider";

const SECTIONS = [
    { id: "voice", label: "Voice", icon: Mic },
    { id: "qoryn", label: "Qoryn", icon: Brain },
    { id: "memory", label: "Memory", icon: Brain },
    { id: "data", label: "Data", icon: Database },
    { id: "account", label: "Account", icon: User },
    { id: "advanced", label: "Advanced", icon: Cpu },
];

function SectionTitle({ children, subtitle }) {
    return (
        <div className="space-y-1">
            <h2 className="font-display text-[22px] font-light tracking-tight text-qoryn-text">
                {children}
            </h2>
            {subtitle && (
                <p className="font-sans text-[13px] text-qoryn-textDim">{subtitle}</p>
            )}
        </div>
    );
}

function Card({ title, description, children, testId }) {
    return (
        <div className="qoryn-panel rounded-2xl p-6 space-y-4" data-testid={testId}>
            <div>
                <h3 className="font-sans text-[15px] text-qoryn-text">{title}</h3>
                {description && (
                    <p className="mt-1 font-sans text-[12px] text-qoryn-textDim leading-relaxed">
                        {description}
                    </p>
                )}
            </div>
            {children}
        </div>
    );
}

/* ---------------- Voice section ---------------- */
function VoiceSection({ settings, onPatch }) {
    const [voices, setVoices] = useState({ voices: [], previewable: [], default: null });
    const [preview, setPreview] = useState(null); // { voice, status }
    const audioRef = useState(() => null)[0];
    const [health, setHealth] = useState(null);

    useEffect(() => {
        fetchVoices().then(setVoices).catch(() => { /* silent */ });
        fetchHealth().then(setHealth).catch(() => { /* silent */ });
    }, []);

    const currentVoice = settings.voice_pref || voices.default || "";
    const greetingEnabled = settings.greeting_enabled ?? (health?.session_greeting_enabled ?? true);

    const stopPreview = () => { setPreview(null); try { window.__qorynPreviewAudio?.pause(); } catch { /* noop */ } };
    const startPreview = async (voice) => {
        if (preview?.voice === voice) { stopPreview(); return; }
        stopPreview();
        setPreview({ voice, status: "loading" });
        try {
            const audio = new Audio(`${API}/realtime/voice-sample?voice=${encodeURIComponent(voice)}`);
            window.__qorynPreviewAudio = audio;
            audio.addEventListener("ended", () => setPreview(null));
            audio.addEventListener("error", () => setPreview({ voice, status: "error" }));
            audio.addEventListener("playing", () => setPreview({ voice, status: "playing" }));
            await audio.play();
        } catch {
            setPreview({ voice, status: "error" });
        }
    };

    return (
        <div className="space-y-4">
            <SectionTitle subtitle="How Qoryn sounds and greets you.">Voice</SectionTitle>

            <Card
                title="Preferred voice"
                description="Applied to every new session unless you override at start time. Preview a voice by clicking play — only voices marked previewable have a TTS sample."
                testId="settings-voice-card"
            >
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {voices.voices.map((v) => {
                        const isSelected = v === currentVoice;
                        const canPreview = voices.previewable.includes(v);
                        const status = preview?.voice === v ? preview.status : null;
                        return (
                            <div
                                key={v}
                                data-testid={`voice-option-${v}`}
                                className={`rounded-xl border px-4 py-3 flex items-center gap-3 transition-colors ${
                                    isSelected
                                        ? "border-qoryn-cyan/60 bg-qoryn-cyan/[0.06]"
                                        : "border-qoryn-border hover:border-qoryn-cyan/30 hover:bg-white/[0.02]"
                                }`}
                            >
                                <button
                                    type="button"
                                    data-testid={`voice-select-${v}`}
                                    onClick={() => onPatch({ voice_pref: v })}
                                    className="flex-1 text-left qoryn-focus"
                                >
                                    <div className="font-sans text-[14px] text-qoryn-text capitalize">{v}</div>
                                    {isSelected && (
                                        <div className="font-mono-ui text-[9px] tracking-widest uppercase text-qoryn-cyan/80">
                                            preferred
                                        </div>
                                    )}
                                </button>
                                <button
                                    type="button"
                                    data-testid={`voice-preview-${v}`}
                                    disabled={!canPreview}
                                    onClick={() => canPreview && startPreview(v)}
                                    className={`shrink-0 h-8 w-8 flex items-center justify-center rounded-full transition-colors qoryn-focus ${
                                        !canPreview
                                            ? "text-qoryn-textMuted/40 cursor-not-allowed"
                                            : status === "playing"
                                                ? "text-qoryn-cyan bg-qoryn-cyan/10"
                                                : "text-qoryn-textDim hover:text-qoryn-cyan hover:bg-white/[0.06]"
                                    }`}
                                    title={canPreview ? "Preview" : "Live-only voice — no preview available"}
                                >
                                    {status === "loading" ? <Loader2 className="h-3 w-3 animate-spin" />
                                        : status === "playing" ? <Pause className="h-3 w-3" strokeWidth={2} />
                                        : <Play className="h-3 w-3" strokeWidth={2} />}
                                </button>
                                {isSelected && <Check className="h-3.5 w-3.5 text-qoryn-cyan shrink-0" />}
                            </div>
                        );
                    })}
                </div>
                {voices.voices.length === 0 && (
                    <p className="font-sans text-[13px] text-qoryn-textDim">Voices unavailable.</p>
                )}
            </Card>

            <Card
                title="Session greeting"
                description="Whether Qoryn opens each new conversation with a short greeting."
                testId="settings-greeting-card"
            >
                <div className="flex items-center gap-3">
                    <button
                        type="button"
                        data-testid="greeting-toggle"
                        role="switch"
                        aria-checked={greetingEnabled}
                        onClick={() => onPatch({ greeting_enabled: !greetingEnabled })}
                        className={`h-6 w-10 rounded-full transition-colors qoryn-focus ${
                            greetingEnabled ? "bg-qoryn-cyan/70" : "bg-white/[0.08]"
                        }`}
                    >
                        <span
                            className={`block h-5 w-5 rounded-full bg-white transition-transform ${
                                greetingEnabled ? "translate-x-4" : "translate-x-0.5"
                            }`}
                        />
                    </button>
                    <div className="flex items-center gap-2 font-sans text-[13px] text-qoryn-textDim">
                        {greetingEnabled ? <Bell className="h-3.5 w-3.5" /> : <BellOff className="h-3.5 w-3.5" />}
                        {greetingEnabled ? "Greeting enabled" : "Greeting disabled"}
                    </div>
                </div>
                <p className="font-mono-ui text-[9px] tracking-widest uppercase text-qoryn-textMuted">
                    Text (server default): &ldquo;I&apos;m here. What are we thinking about?&rdquo;
                </p>
            </Card>
        </div>
    );
}

/* ---------------- Qoryn (instruction overlay) section ---------------- */
function QorynSection({ settings, onSaved }) {
    const [overlay, setOverlay] = useState(settings.prompt_overlay || "");
    const [responseStyle, setResponseStyle] = useState(settings.response_style || "");
    const [preview, setPreview] = useState(null);
    const [saving, setSaving] = useState(false);
    const [savedFlash, setSavedFlash] = useState(false);

    useEffect(() => { setOverlay(settings.prompt_overlay || ""); }, [settings.prompt_overlay]);
    useEffect(() => { setResponseStyle(settings.response_style || ""); }, [settings.response_style]);
    useEffect(() => { getPromptPreview().then(setPreview).catch(() => { /* noop */ }); }, [settings.prompt_overlay]);

    const save = async () => {
        setSaving(true);
        try {
            const updated = await patchSettings({ prompt_overlay: overlay, response_style: responseStyle });
            onSaved(updated);
            setSavedFlash(true);
            setTimeout(() => setSavedFlash(false), 1500);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="space-y-4">
            <SectionTitle subtitle="Adjust how Qoryn thinks and responds — layered on top of the version-controlled base instructions.">
                Qoryn
            </SectionTitle>

            <Card
                title="Instruction overlay"
                description="A LOWER-AUTHORITY per-user preference layer, composed at session-mint time AFTER the capability manifest, active-project context, base prompt, and every version-controlled policy. It can adjust tone, verbosity, address-form, and brainstorm style. It CANNOT override safety, capability truth, provenance rules, decision/idea distinctions, or any policy asset — those higher-authority sections always take precedence. Max 2000 characters."
                testId="settings-overlay-card"
            >
                <textarea
                    data-testid="prompt-overlay-input"
                    value={overlay}
                    onChange={(e) => setOverlay(e.target.value)}
                    maxLength={2000}
                    rows={8}
                    placeholder={"Examples:\n• Prefer terse, honest answers over pleasantries.\n• When exploring, name the strongest counter-argument yourself.\n• Address me as 'Founder' rather than by name."}
                    className="w-full px-3 py-2 rounded-md bg-black/40 border border-qoryn-border focus:border-qoryn-cyan/60 outline-none font-sans text-[13px] text-qoryn-text placeholder:text-qoryn-textMuted qoryn-focus resize-vertical"
                />
                <div className="flex items-center justify-between font-mono-ui text-[9px] tracking-widest uppercase text-qoryn-textMuted">
                    <span>{overlay.length}/2000 chars · lower-authority</span>
                    <span>
                        base sha256: {preview?.base_sha256?.slice(0, 12)}…
                        {" · "}
                        composed: {preview?.composed_sha256?.slice(0, 12)}…
                        {" · "}
                        overlay {preview?.overlay_included ? "APPLIED" : "not applied"}
                    </span>
                </div>
            </Card>

            <Card
                title="Response style"
                description="Short, natural-language hints layered into every session. Kept small on purpose — the overlay above handles longer instructions."
                testId="settings-style-card"
            >
                <input
                    data-testid="response-style-input"
                    value={responseStyle}
                    onChange={(e) => setResponseStyle(e.target.value)}
                    maxLength={2000}
                    placeholder="e.g. brisk, no-fluff, willing to disagree"
                    className="w-full h-10 px-3 rounded-md bg-black/40 border border-qoryn-border focus:border-qoryn-cyan/60 outline-none font-sans text-[13px] text-qoryn-text placeholder:text-qoryn-textMuted qoryn-focus"
                />
            </Card>

            <div className="flex items-center gap-2">
                <button
                    type="button"
                    data-testid="qoryn-settings-save"
                    onClick={save}
                    disabled={saving}
                    className="h-9 px-4 rounded-md bg-qoryn-cyan text-black font-mono-ui text-[10px] tracking-widest uppercase inline-flex items-center gap-2 hover:brightness-110 disabled:opacity-50 qoryn-focus"
                >
                    {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" strokeWidth={2} />}
                    {saving ? "Saving" : "Save"}
                </button>
                {savedFlash && (
                    <span data-testid="qoryn-settings-saved" className="font-mono-ui text-[10px] tracking-widest uppercase text-qoryn-cyan">
                        saved · applies to next session
                    </span>
                )}
            </div>
        </div>
    );
}

/* ---------------- Account section ---------------- */
function AccountSection() {
    const { user, signOut } = useAuth();
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [showPw, setShowPw] = useState(false);
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState(null);
    const [error, setError] = useState(null);

    const submit = async (e) => {
        e.preventDefault();
        setError(null); setMsg(null);
        if (newPassword.length < 8) { setError("Password must be at least 8 characters."); return; }
        if (newPassword !== confirmPassword) { setError("Passwords do not match."); return; }
        setBusy(true);
        try {
            await updatePassword(newPassword);
            setNewPassword(""); setConfirmPassword("");
            setMsg("Password updated.");
        } catch (err) {
            setError(err?.message || String(err));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="space-y-4">
            <SectionTitle subtitle="Your Qoryn identity.">Account</SectionTitle>

            <Card title="Signed in as" testId="settings-account-identity">
                <div className="flex items-center gap-3">
                    <div className="h-8 w-8 rounded-full bg-qoryn-cyan/10 flex items-center justify-center">
                        <User className="h-4 w-4 text-qoryn-cyan" strokeWidth={1.75} />
                    </div>
                    <div className="min-w-0">
                        <div className="font-sans text-[14px] text-qoryn-text truncate" data-testid="account-email">
                            {user?.email}
                        </div>
                        <div className="font-mono-ui text-[9px] tracking-widest uppercase text-qoryn-textMuted">
                            uid {user?.id?.slice(0, 8)}…
                        </div>
                    </div>
                </div>
            </Card>

            <Card title="Change password" description="Password is updated via Supabase Auth. Other active sessions may be signed out." testId="settings-account-password">
                <form onSubmit={submit} className="space-y-3">
                    <div className="relative">
                        <input
                            data-testid="account-new-password"
                            type={showPw ? "text" : "password"}
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            required
                            minLength={8}
                            placeholder="New password (min 8 chars)"
                            autoComplete="new-password"
                            className="w-full h-10 px-3 pr-10 rounded-md bg-black/40 border border-qoryn-border focus:border-qoryn-cyan/60 outline-none font-sans text-[14px] text-qoryn-text qoryn-focus"
                        />
                        <button
                            type="button"
                            onClick={() => setShowPw((v) => !v)}
                            className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 flex items-center justify-center text-qoryn-textMuted hover:text-qoryn-text qoryn-focus"
                            aria-label={showPw ? "Hide password" : "Show password"}
                        >
                            {showPw ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                        </button>
                    </div>
                    <input
                        data-testid="account-confirm-password"
                        type={showPw ? "text" : "password"}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        required
                        minLength={8}
                        placeholder="Confirm"
                        autoComplete="new-password"
                        className="w-full h-10 px-3 rounded-md bg-black/40 border border-qoryn-border focus:border-qoryn-cyan/60 outline-none font-sans text-[14px] text-qoryn-text qoryn-focus"
                    />
                    {error && (
                        <div data-testid="account-password-error" className="rounded-md border border-red-500/40 bg-red-500/[0.06] px-3 py-2 font-mono-ui text-[10px] tracking-wider text-red-300">
                            {error}
                        </div>
                    )}
                    {msg && (
                        <div data-testid="account-password-msg" className="rounded-md border border-qoryn-cyan/40 bg-qoryn-cyan/[0.06] px-3 py-2 font-mono-ui text-[10px] tracking-wider text-qoryn-cyan">
                            {msg}
                        </div>
                    )}
                    <button
                        type="submit"
                        data-testid="account-password-submit"
                        disabled={busy}
                        className="h-9 px-4 rounded-md bg-qoryn-cyan text-black font-mono-ui text-[10px] tracking-widest uppercase inline-flex items-center gap-2 hover:brightness-110 disabled:opacity-50 qoryn-focus"
                    >
                        {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <KeyRound className="h-3.5 w-3.5" strokeWidth={2} />}
                        Update password
                    </button>
                </form>
            </Card>

            <Card title="Session" testId="settings-account-session">
                <button
                    type="button"
                    data-testid="settings-signout-btn"
                    onClick={signOut}
                    className="h-9 px-4 rounded-md border border-qoryn-border text-qoryn-textDim hover:text-red-300 hover:border-red-500/40 hover:bg-red-500/[0.04] font-mono-ui text-[10px] tracking-widest uppercase inline-flex items-center gap-2 transition-colors qoryn-focus"
                >
                    <LogOut className="h-3.5 w-3.5" strokeWidth={2} />
                    Sign out
                </button>
            </Card>
        </div>
    );
}

/* ---------------- Memory & Data (honest placeholders) ---------------- */
function MemorySection() {
    return (
        <div className="space-y-4">
            <SectionTitle subtitle="Retention and recall controls.">Memory</SectionTitle>
            <EmptyState
                icon={Brain}
                title="Memory controls arrive with persistence."
                body="Approve/reject thresholds, per-project recall rules, and archival policies will live here once Phase 1C/1D lands. Nothing here today would be honest to wire &mdash; we don&apos;t retain conversations across sessions yet."
                footnote="arrives in phase 1c / 1d"
            />
        </div>
    );
}

function DataSection() {
    return (
        <div className="space-y-4">
            <SectionTitle subtitle="Retention, export, deletion.">Data</SectionTitle>
            <EmptyState
                icon={Database}
                title="Data controls arrive with persistence."
                body="Session retention windows, transcript export, and bulk deletion require durable session storage — that's Phase 1C. Until then only the current session's in-memory transcript exists, and it's already exportable from the Command Palette (Cmd/Ctrl+K)."
                footnote="arrives in phase 1c"
            />
        </div>
    );
}

/* ---------------- Advanced section ---------------- */
function AdvancedSection() {
    const [health, setHealth] = useState(null);
    const [caps, setCaps] = useState(null);
    const [preview, setPreview] = useState(null);
    useEffect(() => {
        fetchHealth().then(setHealth).catch(() => { /* noop */ });
        fetchCapabilities().then(setCaps).catch(() => { /* noop */ });
        getPromptPreview().then(setPreview).catch(() => { /* noop */ });
    }, []);

    return (
        <div className="space-y-4">
            <SectionTitle subtitle="Under-the-hood diagnostics. The only place where model / provider / voice names appear in the UI.">
                Advanced
            </SectionTitle>

            <FounderCapabilityStatusView />

            <Card title="Realtime provider" testId="settings-advanced-runtime">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-6 font-mono-ui text-[10px] tracking-widest uppercase text-qoryn-textDim">
                    <div>Model</div>       <div className="text-qoryn-text">{health?.realtime_model ?? "—"}</div>
                    <div>Default voice</div><div className="text-qoryn-text">{health?.default_voice ?? "—"}</div>
                    <div>Turn detection</div><div className="text-qoryn-text">{health?.turn_detection_mode ?? "—"}</div>
                    <div>Eagerness</div>   <div className="text-qoryn-text">{health?.turn_detection_eagerness ?? "—"}</div>
                    <div>Provider</div>    <div className="text-qoryn-text">openai</div>
                </div>
            </Card>

            <Card title="Composed prompt" testId="settings-advanced-prompt">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-2 gap-x-6 font-mono-ui text-[10px] tracking-widest uppercase text-qoryn-textDim">
                    <div>Base length</div>       <div className="text-qoryn-text">{preview?.base_length ?? "—"}</div>
                    <div>Composed length</div>   <div className="text-qoryn-text">{preview?.composed_length ?? "—"}</div>
                    <div>Base sha256</div>       <div className="text-qoryn-text truncate">{preview?.base_sha256 ?? "—"}</div>
                    <div>Composed sha256</div>   <div className="text-qoryn-text truncate">{preview?.composed_sha256 ?? "—"}</div>
                    <div>Overlay applied</div>   <div className="text-qoryn-text">{preview ? (preview.overlay_included ? "YES" : "no") : "—"}</div>
                    <div>Overlay length</div>    <div className="text-qoryn-text">{preview?.overlay_length ?? 0}</div>
                </div>
            </Card>

            <Card title="Capability manifest" description="Live-loaded on every session mint; drives the authoritative section of Qoryn's runtime instructions." testId="settings-advanced-capabilities">
                <ul className="space-y-1.5">
                    {caps?.capabilities?.map((c) => (
                        <li key={c.id} className="flex items-center gap-3">
                            <span className={`h-1.5 w-1.5 rounded-full ${c.enabled ? "bg-qoryn-cyan" : "bg-white/10"}`} />
                            <span className="font-sans text-[13px] text-qoryn-text w-52 shrink-0">{c.label}</span>
                            <span className={`font-mono-ui text-[9px] tracking-widest uppercase ${c.enabled ? "text-qoryn-cyan" : "text-qoryn-textMuted"}`}>
                                {c.enabled ? "ENABLED" : "disabled"}
                            </span>
                        </li>
                    ))}
                    {!caps && <li className="font-sans text-[13px] text-qoryn-textDim">loading…</li>}
                </ul>
            </Card>

            <Card title="Processing jobs" description="Async work queue (summarization, embedding). Empty until Phase 1C wires it in." testId="settings-advanced-jobs">
                <EmptyState icon={Cpu} title="No jobs yet." footnote="arrives with summarization pipeline (phase 1c)" />
            </Card>
        </div>
    );
}

/* ---------------- Page shell ---------------- */
export default function SettingsPage() {
    const { section: paramSection } = useParams();
    const navigate = useNavigate();
    const active = useMemo(
        () => SECTIONS.find((s) => s.id === paramSection)?.id ?? "voice",
        [paramSection],
    );
    const [settings, setSettings] = useState(null);
    const [error, setError] = useState(null);

    const load = useCallback(() => {
        getSettings().then(setSettings).catch((e) => setError(e?.message || String(e)));
    }, []);
    useEffect(() => { load(); }, [load]);

    const patch = useCallback(async (p) => {
        const updated = await patchSettings(p);
        setSettings(updated);
    }, []);

    return (
        <div data-testid="settings-page" className="space-y-6">
            <h1 className="font-display text-[28px] font-light tracking-tight text-qoryn-text">Settings</h1>

            <div className="flex items-center gap-1 border-b border-qoryn-border/50" role="tablist" data-testid="settings-tabs">
                {SECTIONS.map((s) => {
                    const isActive = active === s.id;
                    const Icon = s.icon;
                    return (
                        <button
                            key={s.id}
                            role="tab"
                            aria-selected={isActive}
                            data-testid={`settings-tab-${s.id}`}
                            onClick={() => navigate(`/settings/${s.id}`)}
                            className={`px-4 py-2 -mb-px border-b-2 font-mono-ui text-[10px] tracking-widest uppercase transition-colors inline-flex items-center gap-2 qoryn-focus ${
                                isActive
                                    ? "border-qoryn-cyan text-qoryn-cyan"
                                    : "border-transparent text-qoryn-textDim hover:text-qoryn-text"
                            }`}
                        >
                            <Icon className="h-3 w-3" strokeWidth={1.75} />
                            {s.label}
                        </button>
                    );
                })}
            </div>

            {/* Settings-independence for /settings/advanced:
                The Advanced section (and specifically the Founder
                capability-status view mounted at its top) MUST render
                whether or not the user-settings payload has loaded.
                A `getSettings` failure blocks Voice / Qoryn / Memory /
                Data / Account (all of which consume `settings`), but
                MUST NOT block the read-only capability status view.
                Advanced consumes only `fetchHealth`, `fetchCapabilities`,
                `getPromptPreview`, and the `useClientCapabilities` hook
                internally, so it renders unconditionally on this tab. */}
            {active === "advanced" ? (
                <div data-active-section={active}>
                    <AdvancedSection />
                </div>
            ) : (
                <>
                    {error && <EmptyState icon={Info} title="Couldn't load settings." body={error} />}
                    {!settings && !error && (
                        <div className="py-10 flex items-center justify-center"><Loader2 className="h-4 w-4 animate-spin text-qoryn-cyan" /></div>
                    )}
                    {settings && (
                        <div data-active-section={active}>
                            {active === "voice" && <VoiceSection settings={settings} onPatch={patch} />}
                            {active === "qoryn" && <QorynSection settings={settings} onSaved={setSettings} />}
                            {active === "memory" && <MemorySection />}
                            {active === "data" && <DataSection />}
                            {active === "account" && <AccountSection />}
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
